import React, { useMemo } from 'react'
import { PageLink as Link } from 'src/app/AppAuth/PageLink'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import {
  ChatCitation,
  ChatContextSourceType,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { formatISODateTime } from 'src/Utils/formatDate'
import { AllContexts } from 'src/pages/TrusteroIntelligence/chatcontextpanel/categoryselector/CategorySelector'
import { ContextTypeSummaryItemIcon } from 'src/pages/TrusteroIntelligence/chatcontextpanel/chatContextPanel.styles'
import {
  DocumentSourceLinkIcon,
  DocumentSourceRelevantDate,
  SourceContainer,
  SourceDeleted,
} from 'src/components/Reusable/Sources/sources.styles'
import { useDocumentSourceContext } from 'src/context/DocumentSource/DocumentSourceContext'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import { useCitationLink } from '../securityQuestionnaire.hooks'
import {
  QASourcesContainer,
  PageNumberLabel,
  PageNumberItemLabel,
  PageNumberItemLabelLink,
} from './securityQuestionnaireShowPage.styles'

const AnswerCitation = ({
  citation,
  isAgentChat,
}: {
  citation: ChatCitation
  isAgentChat?: boolean
}): JSX.Element => {
  const { openModal } = useModalState()
  const { setCurrentDocumentSource } = useDocumentSourceContext()
  const citationLink = useCitationLink(citation)
  const caption = citation.getCaption()
  const sourceType = citation.getType()
  const date = citation.getRelevantDate()
  const isDeleted = citation.getIsDeleted()
  const isDocumentSource =
    sourceType === ChatContextSourceType.DOCUMENT_GROUP ||
    sourceType === ChatContextSourceType.KNOWLEDGE_BASE ||
    sourceType === ChatContextSourceType.ARTIFACT_DOCUMENT ||
    sourceType == ChatContextSourceType.CHAT_UPLOAD_DOCUMENT ||
    (sourceType === ChatContextSourceType.SUPPORT_KNOWLEDGE &&
      !!citation.getContentId()) ||
    (sourceType === ChatContextSourceType.COMPANY &&
      !caption.startsWith('Scope Info for account -'))
  const onClick = () => {
    if (isDocumentSource) {
      setCurrentDocumentSource(citation)
      openModal(ModalFormId.VIEW_DOCUMENT_SOURCE)
    }
  }
  // A prior answer no longer indexed has nowhere to go, and an empty link
  // would open the page you are already on.
  const isUnlocatedPriorAnswer =
    sourceType === ChatContextSourceType.INBOUND_QUESTIONNAIRE && !citationLink

  const contextTypeInfo = useMemo(
    () => AllContexts.find((context) => context.type === citation.getType()),
    [citation],
  )

  const icon = useMemo(() => contextTypeInfo?.icon, [contextTypeInfo])

  const tooltipBody = useMemo(
    () => `${caption} (${contextTypeInfo?.label})`,
    [caption, contextTypeInfo],
  )

  // SOC2 Report Summary feature is hidden — skip rendering its citations
  if (sourceType === ChatContextSourceType.SOC2_REPORT_SUMMARY) {
    return <></>
  }

  return (
    <SourceContainer $isAgentChat={isAgentChat}>
      <FlexRow gap={8} justify={FlexAlign.FLEX_START}>
        <Tooltip
          key={citation.getType()}
          id={`tooltip-context-edit-${citation.getType()}`}
          trigger={['hover', 'click']}
          tooltipBody={tooltipBody}
          placement="left"
        >
          <ContextTypeSummaryItemIcon $small>{icon}</ContextTypeSummaryItemIcon>
        </Tooltip>
        <span>{caption}</span>
        {date && (
          <DocumentSourceRelevantDate $isAgentChat={isAgentChat}>
            {formatISODateTime(new Date(date))}
          </DocumentSourceRelevantDate>
        )}
        {isDeleted ? (
          <SourceDeleted>(Deleted)</SourceDeleted>
        ) : isDocumentSource ? (
          <CitationLink
            isAgentChat={isAgentChat}
            mimeType={citation.getMimetype()}
            citationType={{
              onClick,
              pageNumbers: citation.getPageNumber()
                ? [
                    {
                      pageNumber: citation.getPageNumber(),
                      onClick,
                    },
                  ]
                : undefined,
            }}
          />
        ) : isUnlocatedPriorAnswer ? null : (
          <CitationLink
            isAgentChat={isAgentChat}
            mimeType={citation.getMimetype()}
            citationType={{
              link: citationLink,
              pageNumbers: citation.getPageNumber()
                ? [
                    {
                      pageNumber: citation.getPageNumber(),
                      link: citationLink,
                    },
                  ]
                : undefined,
            }}
          />
        )}
      </FlexRow>
    </SourceContainer>
  )
}

export type AnswerCitationsProps = {
  citations?: ChatCitation[]
  mt?: number
  isAgentChat?: boolean
}

export const AnswerCitations = ({
  citations: allCitations,
  mt = 0,
  isAgentChat = false,
}: AnswerCitationsProps): JSX.Element | null => {
  const citations = useMemo(
    () => allCitations?.filter((citation) => citation.getCaption()),
    [allCitations],
  )

  if (!citations || citations.length === 0) {
    return null
  }

  return (
    <>
      {citations.length > 0 && (
        <QASourcesContainer $mt={mt} $isAgentChat={isAgentChat}>
          {!isAgentChat && <div>Evidence and Sources</div>}
          {/* Grading's policy citations carry no reference id, so the position
              keeps their keys apart. */}
          {citations.map((citation, index) => (
            <AnswerCitation
              key={`${citation.getType()}-${citation.getReferenceId()}-${index}`}
              citation={citation}
              isAgentChat={isAgentChat}
            />
          ))}
        </QASourcesContainer>
      )}
    </>
  )
}

export type InlinePageNumberType = {
  pageNumber: number
  onClick: () => void
}

export type InlineCitationType = {
  onClick: () => void
  pageNumbers?: InlinePageNumberType[]
}

export type LinkPageNumberType = {
  pageNumber: number
  link: string
}

export type LinkCitationType = {
  link: string
  pageNumbers?: LinkPageNumberType[]
}

export type CitationType = InlineCitationType | LinkCitationType

const isInlineCitationType = (
  citationType: CitationType,
): citationType is InlineCitationType => {
  return 'onClick' in citationType
}

const isLinkCitationType = (
  citationType: CitationType,
): citationType is LinkCitationType => {
  return 'link' in citationType
}

const isLinkPageNumberType = (
  pageNumber: LinkPageNumberType | InlinePageNumberType,
): pageNumber is LinkPageNumberType => {
  return 'link' in pageNumber
}

export type CitationLinkProps = {
  citationType: CitationType
  mimeType?: string
  isAgentChat?: boolean
}

export const CitationLink = ({
  citationType,
  mimeType,
  isAgentChat = false,
}: CitationLinkProps): JSX.Element | null => {
  if (isInlineCitationType(citationType)) {
    return (
      <>
        <CitationPageNumber
          pageNumbers={citationType.pageNumbers}
          mimeType={mimeType}
          isAgentChat={isAgentChat}
        />
        <DocumentSourceLinkIcon
          onClick={(e) => {
            e.stopPropagation()
            citationType.onClick()
          }}
        />
      </>
    )
  }

  if (isLinkCitationType(citationType)) {
    return (
      <>
        <CitationPageNumber
          pageNumbers={citationType.pageNumbers}
          mimeType={mimeType}
          isAgentChat={isAgentChat}
        />
        <Link to={citationType.link} target="_blank" rel="noreferrer">
          <DocumentSourceLinkIcon />
        </Link>
      </>
    )
  }

  return null
}

export type CitationPageNumberProps = {
  pageNumbers?: LinkPageNumberType[] | InlinePageNumberType[]
  mimeType?: string
  isAgentChat?: boolean
}

export const CitationPageNumber = ({
  pageNumbers,
  mimeType,
  isAgentChat = false,
}: CitationPageNumberProps): JSX.Element | null => {
  const isPdf = mimeType === 'application/pdf'

  const pageNumberItems = useMemo(() => {
    if (!pageNumbers) {
      return []
    }
    return pageNumbers
      .sort((a, b) => a.pageNumber - b.pageNumber)
      .map((pageNumberItem, index) => (
        <div
          key={pageNumberItem.pageNumber}
          style={{ display: 'inline-block' }}
        >
          {isLinkPageNumberType(pageNumberItem) ? (
            <PageNumberItemLabelLink
              to={pageNumberItem.link}
              target="_blank"
              rel="noreferrer"
              $isAgentChat={isAgentChat}
            >
              {pageNumberItem.pageNumber}
            </PageNumberItemLabelLink>
          ) : (
            <PageNumberItemLabel
              $isAgentChat={isAgentChat}
              onClick={pageNumberItem.onClick}
            >
              {pageNumberItem.pageNumber}
            </PageNumberItemLabel>
          )}
          {index < pageNumbers.length - 1 && <span> | </span>}
        </div>
      ))
  }, [pageNumbers, isAgentChat])

  if (!pageNumbers?.length || !isPdf) {
    return null
  }

  return (
    <PageNumberLabel $isAgentChat={isAgentChat}>
      (PDF Page {pageNumberItems})
    </PageNumberLabel>
  )
}
